<template>
    <div class="wheel-picker-container" v-if="hunts && hunts.length">
      <div class="wheel-picker" ref="wheelPicker" @scroll.passive="onScroll">
        <div class="scroll-hint">Scroll to browse hunts</div>
        
        <!-- Spacer to allow scrolling to first item -->
        <div class="wheel-spacer"></div>
  
        <div
          v-for="(hunt, index) in hunts"
          :key="hunt.huntId"
          :class="['wheel-item', { 'wheel-item-selected': index === selectedIndex }]"
          @click="selectItem(index, $event)"
        >
          <div :class="['hunt-unit', getTextLengthClass(hunt)]">
            {{ hunt.huntName }}, {{ hunt.unitName }}, {{ hunt.huntId }}
          </div>
          <!-- Information icon - outside hunt-unit div for consistent sizing -->
          <span 
            v-if="index === selectedIndex" 
            class="info-icon"
            @click.stop="toggleBioCard"
          >
            <i class="mdi mdi-information-outline"></i>
          </span>
        </div>
        
        <!-- Spacer to allow scrolling to last item -->
        <div class="wheel-spacer"></div>
      </div>
    </div>
  
    <div v-else>
      <!-- No hunts to show -->
    </div>
  </template>
  
  <script>
  import { defineComponent, ref, nextTick, onMounted } from "vue";
  import "@mdi/font/css/materialdesignicons.css";
  import { mapState } from 'vuex';

  export default defineComponent({
    name: "HuntWheelSelector",
    props: {
      hunts: {
        type: Array,
        required: true,
      },
    },
    emits: ['hunt-selected', 'toggle-bio-card', 'error'],
    setup(props, { emit }) {
      const wheelPicker = ref(null);
      const selectedIndex = ref(0);
      let scrollTimeout = null;
  
  
      /**
       * Handles scroll events efficiently.
       * Uses requestAnimationFrame to prevent excessive calculations.
       */
      function onScroll() {
        if (scrollTimeout) {
          cancelAnimationFrame(scrollTimeout);
        }
        scrollTimeout = requestAnimationFrame(() => {
          updateSelectedBasedOnPosition();
        });
      }
  
      /**
       * Determines which item is closest to the center of the scroll container.
       */
      function updateSelectedBasedOnPosition() {
        const container = wheelPicker.value;
        if (!container) return;
  
        const items = container.querySelectorAll(".wheel-item");
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;
  
        let closestIndexVal = 0;
        let closestDistance = Infinity;
  
        items.forEach((item, idx) => {
          const itemRect = item.getBoundingClientRect();
          const itemCenter = itemRect.top + itemRect.height / 2;
          const distance = Math.abs(itemCenter - containerCenter);
  
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndexVal = idx;
          }
        });
  
        if (closestIndexVal !== selectedIndex.value) {
          selectedIndex.value = closestIndexVal;
          // Emit the selected hunt
          if (props.hunts && props.hunts[closestIndexVal]) {
            emit('hunt-selected', props.hunts[closestIndexVal]);
          }
        }
      }
  
      /**
       * If a user clicks an item, we smoothly scroll it into center.
       */
      function selectItem(index, event) {
        selectedIndex.value = index;
        
        // Emit the selected hunt
        if (props.hunts && props.hunts[index]) {
          emit('hunt-selected', props.hunts[index]);
        }
  
        nextTick(() => {
          const container = wheelPicker.value;
          if (!container) return;
  
          const itemEl = event.currentTarget;
          if (!itemEl) return;
  
          const containerRect = container.getBoundingClientRect();
          const itemRect = itemEl.getBoundingClientRect();
          const containerCenter = containerRect.top + containerRect.height / 2;
          const itemCenter = itemRect.top + itemRect.height / 2;
  
          let offset = itemCenter - containerCenter;
          const maxScroll = container.scrollHeight - container.clientHeight;
          let newScrollTop = container.scrollTop + offset;
  
          if (newScrollTop < 0) newScrollTop = 0;
          if (newScrollTop > maxScroll) newScrollTop = maxScroll;
  
          container.scrollTo({ top: newScrollTop, behavior: "smooth" });
        });
      }
  
      onMounted(() => {
        updateSelectedBasedOnPosition();
        
        // Emit the initially selected hunt
        if (props.hunts && props.hunts.length > 0) {
          emit('hunt-selected', props.hunts[0]);
        }
      });
  
      /**
       * Toggle the bio card when the info icon is clicked
       */
      function toggleBioCard() {
        emit('toggle-bio-card');
      }

      /**
       * Determine the appropriate CSS class based on text length
       * Ensures proper font sizing to prevent text from overlapping with icons
       * Uses more granular size adjustments to maximize font size while fitting
       */
      function getTextLengthClass(hunt) {
        if (!hunt) return '';
        
        const fullText = `${hunt.huntName}, ${hunt.unitName}, ${hunt.huntId}`;
        const textLength = fullText.length;
        
        // More granular size adjustments
        if (textLength > 80) {
          return 'size-xs'; // Extra small text
        } else if (textLength > 70) {
          return 'size-s'; // Small text
        } else if (textLength > 45) {
          return 'size-m'; // Medium text
        } else if (textLength > 25) {
          return 'size-l'; // Large text
        } else {
          return 'size-xl'; // Extra large text (default)
        }
      }

      return {
        wheelPicker,
        selectedIndex,
        onScroll,
        selectItem,
        toggleBioCard,
        getTextLengthClass
      };
    },
  });
  </script>
  
<style scoped>
.wheel-picker-container {
  margin-top: 0.5rem;
  width: 100%;
  position: relative;
  height: 350px; /* Match the parent container height minus header */
  max-height: calc(100% - 40px); /* Account for the header in the parent */
}

.wheel-picker {
  height: 100%;
  padding: 0; /* Remove padding to allow full scrolling */
  overflow-y: auto;
  position: relative;
  perspective: 800px;
  scrollbar-width: thin;
  scrollbar-color: #007bff #f0f0f0;
  -webkit-overflow-scrolling: touch;
}

.wheel-spacer {
  height: 120px; /* Fixed height of 2 items to ensure proper scrolling to first/last item */
  min-height: 60px; /* Minimum height to ensure scrollability */
}

/* Mobile-specific wheel adjustments */
@media (max-width: 480px) {
  .wheel-item {
    height: 70px; /* Slightly taller on mobile to fit wrapping content */
  }
  
  .hunt-unit {
    max-height: 60px; /* Adjust max height for mobile */
    padding-right: 45px; /* Right padding for the info icon on mobile */
  }
}

.scroll-hint {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  text-align: center;
  color: #666;
  font-size: 0.8rem;
  padding: 2px 0;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  z-index: 5;
  opacity: 0.8;
  margin: 0;
}

.wheel-picker::-webkit-scrollbar {
  width: 6px;
}
.wheel-picker::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}
.wheel-picker::-webkit-scrollbar-thumb {
  background-color: #007bff;
  border-radius: 3px;
}

.wheel-item {
  margin: 8px 0;
  transition: transform 0.3s ease, opacity 0.3s ease, filter 0.3s ease;
  transform-origin: center;
  cursor: pointer;
  height: 65px; /* Slightly increased height to accommodate up to 3 lines */
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  position: relative; /* Added for absolute positioning of the info icon */
}

.wheel-item:not(.wheel-item-selected) {
  transform: scale(0.9) translateZ(-30px);
  opacity: 0.6;
  filter: blur(1px);
}

.wheel-item-selected {
  transform: scale(1) translateZ(0);
  opacity: 1;
  filter: none;
  z-index: 10;
  border-left: 3px solid #007bff;
}

.hunt-unit {
  font-size: 1.2rem;
  font-weight: 600;
  text-align: left;
  padding: 10px;
  padding-right: 40px; /* Reduced padding for just the info icon */
  width: 100%;
  /* Allow text to wrap if needed */
  word-wrap: break-word;
  word-break: break-word;
  /* Ensure text stays within container */
  line-height: 1.2;
  position: relative;
  overflow: hidden;
  max-height: 55px; /* Slightly less than wheel-item height to ensure containment */
  display: -webkit-box;
  -webkit-line-clamp: 3; /* Limit to 3 lines */
  -webkit-box-orient: vertical;
}

.info-icon {
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  color: #007bff;
  cursor: pointer;
  transition: color 0.2s ease;
  font-size: 1.5rem; /* Fixed font size for consistency regardless of text size */
}

.info-icon:hover {
  color: #0056b3;
}

/* Responsive font size adjustment based on text length and screen size */
.hunt-unit.size-xl {
  font-size: 1.1rem;
}

.hunt-unit.size-l {
  font-size: 1rem;
}

.hunt-unit.size-m {
  font-size: 0.9rem;
}

.hunt-unit.size-s {
  font-size: 0.8rem;
}

.hunt-unit.size-xs {
  font-size: 0.7rem;
}

/* Mobile-specific font size adjustments */
@media (max-width: 767px) {
  .hunt-unit.size-xl {
    font-size: 1.5rem;
  }

  .hunt-unit.size-l {
    font-size: 1.2rem;
  }

  .hunt-unit.size-m {
    font-size: 0.9rem;
  }

  .hunt-unit.size-s {
    font-size: 0.75rem;
  }

  .hunt-unit.size-xs {
    font-size: 0.7rem;
  }
}

.wheel-item:nth-child(odd) {
  background-color: #f0f0f0;
}

.wheel-item:nth-child(even) {
  background-color: #ffffff;
}
</style>
